import { getSizedCalmAssetUrl } from '@/utils/images/imageLoader';
import { DS_HEIGHT } from '@calm/evexia-design-tokens';
import { getInitials } from '@calm/evexia-web-common/dist/name';
import { Avatar, AvatarProps, SvgIcon } from '@rhg/libs/material';
import { ProfileFilled } from '@rhg/libs/material/icons';
import NextImage from '@rhg/libs/next/image';
import { memo, ReactElement } from 'react';
import { Contributor } from '../types';

/**
 * Largest DS avatar (96px), used as the `next/image` display-width fallback
 * when no explicit dimensions are available — replacing the old 1024px default.
 * `next/image` still requests a 2× (192px) rendition for retina, which the
 * headshot src cap ({@link AVATAR_MAX_SRC_PX}) permits.
 */
const AVATAR_FALLBACK_PX = DS_HEIGHT.AVATAR_XLARGE;

/**
 * Width requested when sizing the headshot URL passed to `<Avatar src>`. Set to
 * 2× the largest avatar (xlarge 96px) so it covers every avatar at 2× DPR. It
 * must stay strictly above the largest rendered size: `next/image` builds a
 * srcSet at 1× and 2× the display width, and if the loader returned the same
 * URL for both (i.e. the cap sat at or below the 2× width) next would warn that
 * the loader "does not implement width". 192 keeps 1× (≤96) and 2× distinct.
 */
const AVATAR_MAX_SRC_PX = DS_HEIGHT.AVATAR_XLARGE * 2;

const styleBackupIcon = (props: {
    height?: string | number;
    width?: string | number;
}) => ({
    height: props.height ?? '64px',
    width: props.width ?? '64px',
});

const styleColorIcon = {
    color: 'primary.light',
};

type AvatarImageOwnerState = {
    sx?: {
        width?: number | `${number}`;
        height?: number | `${number}`;
    };
};

const AvatarImage = ({
    src,
    ownerState,
    ...props
}: React.ImgHTMLAttributes<HTMLImageElement> & {
    ownerState?: AvatarImageOwnerState;
}): ReactElement => {
    // We want to ensure that only strings can be used as the src for the image. If src is not a string, we will render a fallback icon instead.
    if (!src || typeof src !== 'string') {
        return (
            <SvgIcon sx={styleBackupIcon(props)}>
                <ProfileFilled sx={styleColorIcon} />
            </SvgIcon>
        );
    }
    const dimensions: {
        width: number | `${number}`;
        height: number | `${number}`;
    } = (() => {
        if (props.width && props.height) {
            const width = Number(props.width);
            const height = Number(props.height);
            // Guard against non-numeric strings (e.g. "50%") becoming NaN; fall
            // through to ownerState/default sizing when they aren't real numbers.
            if (Number.isFinite(width) && Number.isFinite(height)) {
                return { width, height };
            }
        }
        const simpleDimensions = ['string', 'number'];
        if (
            ownerState?.sx?.width &&
            simpleDimensions.includes(typeof ownerState.sx.width) &&
            ownerState.sx.height &&
            simpleDimensions.includes(typeof ownerState.sx.height)
        ) {
            return {
                width: ownerState.sx.width,
                height: ownerState.sx.height,
            };
        }
        return { width: AVATAR_FALLBACK_PX, height: AVATAR_FALLBACK_PX };
    })();
    return (
        <NextImage
            {...props}
            src={src}
            alt={props.alt ?? ''}
            width={dimensions.width}
            height={dimensions.height}
            style={{ width: 'auto', height: '100%' }}
        />
    );
};

type ContributorAvatarProps = {
    contributor?: Contributor;
} & AvatarProps;

/**
 * Skip a re-render unless the *rendered* avatar content actually changes.
 * `contributor` is compared by its display fields (headshot URL + name) rather
 * than reference, so a fresh-but-equal `contributor` object — e.g. from a pack
 * refetch — does NOT force a re-render. Every other prop is shallow-compared.
 */
function contributorAvatarPropsAreEqual(
    prev: ContributorAvatarProps,
    next: ContributorAvatarProps
): boolean {
    const { contributor: prevContributor, ...prevRest } = prev;
    const { contributor: nextContributor, ...nextRest } = next;
    if (
        prevContributor?.headshot?.url !== nextContributor?.headshot?.url ||
        prevContributor?.name !== nextContributor?.name
    ) {
        return false;
    }
    const prevKeys = Object.keys(prevRest) as (keyof typeof prevRest)[];
    const nextKeys = Object.keys(nextRest);
    if (prevKeys.length !== nextKeys.length) {
        return false;
    }
    return prevKeys.every((key) => prevRest[key] === nextRest[key]);
}

/**
 * Memoized: MUI `<Avatar>` re-runs `useLoaded` (a `new Image()` preload) on
 * re-render, which in Safari visibly re-decodes/flashes the headshot. Skipping
 * re-renders when the displayed content is unchanged keeps the avatar from
 * flashing on unrelated churn (play/pause, pack refetch). EVE-13080.
 */
export const ContributorAvatar = memo(function ContributorAvatar({
    contributor,
    alt,
    ...props
}: ContributorAvatarProps) {
    const [firstName, lastName] = contributor?.name?.split(/\s/) ?? [];
    const initials = getInitials({ firstName, lastName });
    // MUI's <Avatar> calls `useLoaded` internally, which preloads `src` via
    // `new Image()` to detect load success before rendering. Passing the raw
    // headshot URL there makes every avatar fetch the full-res original
    // (often 2000×2000) on top of the sized image `AvatarImage` renders. Cap
    // the URL at 2× the largest avatar so the preload and the rendered image
    // stay small while `next/image` can still scale a distinct 1×/2× srcSet.
    const headshotUrl = contributor?.headshot?.url;
    const sizedHeadshotUrl = headshotUrl
        ? getSizedCalmAssetUrl(headshotUrl, AVATAR_MAX_SRC_PX)
        : undefined;
    return (
        <Avatar
            src={sizedHeadshotUrl}
            {...props}
            alt={alt ?? ''}
            data-testid={`AVATAR_${initials}`}
            slots={{ img: AvatarImage }}
        >
            {initials}
        </Avatar>
    );
},
contributorAvatarPropsAreEqual);
